import { r as createLogger } from "../logger-CdXeOlrX.js";
import { writeFileOrAbort } from "./write-file-or-abort.js";
import { getSessionsRootHandle } from "./opfs-storage.js";
import { assertSafeZipEntryPaths } from "./zip-entry-path.js";
import { classifyPickerRejection } from "./picker-errors.js";
import { BlobReader, BlobWriter, ZipWriter } from "@zip.js/zip.js";
//#region ../src/storage/zip-export.ts
/**
* ZIP Export Module
*
* Exports OPFS session data as ZIP files for download.
* Uses @zip.js/zip.js library for streaming ZIP creation and reading.
*
* The ZIP uses "store" mode (no compression) because:
* 1. Images are already compressed (JPEG)
* 2. Faster packaging
* 3. Simpler implementation
*
* @zip.js/zip.js was chosen over fflate because:
* - Supports streaming reads and writes (needed for periodic sync to external file)
* - Can append to existing ZIP files via prependZip
* - Actively maintained with good TypeScript support
* - Built-in Web Worker support for non-blocking compression
*/
const log = createLogger("ZipExport");
/**
* Get the OPFS sessions directory handle from the layout owner
* (quality-review G-9 — this used to re-derive the
* `'gps-plus-slam'/'sessions'` path itself, so a root re-nesting in
* `opfs-storage.ts` would have silently broken export only).
*/
function getSessionsHandle() {
	const sessions = getSessionsRootHandle();
	if (!sessions) throw new Error("OPFS storage not initialized — call initOpfsStorage() before exporting");
	return sessions;
}
/**
* Stream all files from a directory recursively into a ZipWriter.
* Files are read one at a time and immediately added to the ZIP,
* keeping memory usage proportional to a single file (not the total).
*
* @returns Number of files added
*/
async function streamDirectoryToZip(dirHandle, zipWriter, basePath = "") {
	let count = 0;
	for await (const entry of dirHandle.values()) {
		const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
		if (entry.kind === "file") {
			const file = await (await dirHandle.getFileHandle(entry.name)).getFile();
			await zipWriter.add(relativePath, new BlobReader(file));
			count++;
		} else if (entry.kind === "directory") {
			const subDirHandle = await dirHandle.getDirectoryHandle(entry.name);
			count += await streamDirectoryToZip(subDirHandle, zipWriter, relativePath);
		}
	}
	return count;
}
/**
* Export a flat-layout session from OPFS as a ZIP blob.
*
* The ZIP structure mirrors the OPFS session structure:
* - session.json (at root)
* - actions/000001.json, actions/000002.json, ...
* - images/frame-000001.jpg, images/frame-000002.jpg, ... (legacy: frames/)
*
* Uses "store" mode (compression level 0) for fast packaging. Consumers with a
* different on-disk layout (one that nests sessions under a named bucket)
* resolve their own session handle and call {@link exportSessionHandleAsZip}.
*
* @param sessionName - Name of the session folder under `sessions/`.
* @returns ZIP export result with blob and file count
* @throws Error if the session is not found
*/
async function exportSessionAsZip(sessionName, options) {
	log.info(`Exporting session: ${sessionName}`);
	const sessionsDir = getSessionsHandle();
	let sessionHandle;
	try {
		sessionHandle = await sessionsDir.getDirectoryHandle(sessionName);
	} catch {
		throw new Error(`Session "${sessionName}" not found in OPFS storage`);
	}
	return exportSessionHandleAsZip(sessionHandle, options);
}
/**
* Stream an already-resolved session directory handle to a ZIP blob, running
* any caller-supplied extension {@link ZipExportContributor}s after the
* framework-owned files.
*
* This is the layout-agnostic core of {@link exportSessionAsZip}: consumers
* that resolve a session directory handle themselves (e.g. an app that nests
* sessions under its own grouping layer) call this directly so the framework's
* ZIP schema stays the single source of truth without the framework needing to
* know how the handle was located.
*
* @param sessionHandle - The session directory to package (must contain the
*   framework-owned `session.json` / `actions/` / `images/` tree).
* @returns ZIP export result with blob and file count.
*/
async function exportSessionHandleAsZip(sessionHandle, options) {
	const zipWriter = new ZipWriter(new BlobWriter("application/zip"), { level: 0 });
	let fileCount = await streamDirectoryToZip(sessionHandle, zipWriter);
	const contributors = options?.contributors ?? [];
	const seenSubdirs = /* @__PURE__ */ new Set();
	for (const contributor of contributors) {
		const subdir = contributor.subdir;
		if (!subdir || subdir.includes("/") || subdir.startsWith(".")) throw new Error(`ZipExportContributor.subdir must be a non-empty single path segment, got: ${JSON.stringify(subdir)}`);
		if (seenSubdirs.has(subdir)) throw new Error(`Duplicate ZipExportContributor.subdir registered: ${subdir}`);
		seenSubdirs.add(subdir);
		const addFile = async (relativePath, blob) => {
			try {
				assertSafeZipEntryPaths([`${subdir}/${relativePath}`]);
			} catch (err) {
				throw new Error(`ZipExportContributor relative path is unsafe: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
			}
			await zipWriter.add(`${subdir}/${relativePath}`, new BlobReader(blob));
		};
		fileCount += await contributor.contribute(addFile);
	}
	const blob = await zipWriter.close();
	log.info(`ZIP created: ${blob.size} bytes, ${fileCount} files`);
	return {
		blob,
		fileCount
	};
}
/**
* Sync current OPFS session data to an external file handle.
*
* This is used for periodic sync during recording to save data to the user's
* chosen location (obtained via showSaveFilePicker). The function:
* 1. Reads all current session data from OPFS
* 2. Creates a ZIP in memory
* 3. Writes the ZIP to the external file handle
*
* This provides crash safety combined with OPFS - even if the app crashes,
* the last synced ZIP contains all data up to that point.
*
* @param fileHandle - File handle from showSaveFilePicker
* @param sessionName - Name of the session folder under `sessions/`
* @returns ZIP export result with blob and file count
* @throws Error if the session is not found
*/
async function syncToExternalZip(fileHandle, sessionName, options) {
	log.info(`Syncing to external ZIP: ${sessionName}`);
	const result = await exportSessionAsZip(sessionName, options);
	await writeFileOrAbort(fileHandle, result.blob);
	log.info(`Synced ${result.blob.size} bytes to external file`);
	return result;
}
/**
* Trigger a file download in the browser.
*
* Uses showSaveFilePicker if available (for better UX on desktop),
* falls back to <a download> for broader compatibility.
*
* @param blob - The blob to download
* @param filename - Suggested filename
* @returns `true` when a download or save was started; `false` when the
*   user dismissed the save picker, in which case nothing was written. A
*   caller whose next step assumes the file exists (the Tour Viewer's
*   finish step) keeps its download control live on `false`; before this
*   result the cancelled path was silent.
*/
async function downloadZip(blob, filename) {
	return downloadBlob(blob, filename, ZIP_FILE_TYPE);
}
/** A recording or tour archive - the type both apps' zips are saved as. */
const ZIP_FILE_TYPE = {
	description: "ZIP Archive",
	mimeType: "application/zip",
	extension: ".zip"
};
/** A printable document - the Tour Viewer's sheet of numbered codes. */
const PDF_FILE_TYPE = {
	description: "PDF Document",
	mimeType: "application/pdf",
	extension: ".pdf"
};
/** A rejection's message for a log line; a non-error is stringified. */
function messageOf(err) {
	return err instanceof Error ? err.message : String(err);
}
/**
* Trigger a file download, with the picker filtered to `fileType`.
*
* Generalised from `downloadZip` when a second kind of file needed the same
* save path (the printable PDF of QR codes): the picker's type filter was
* the only zip-specific thing in it, and a second copy of the
* picker-then-anchor dance is exactly the duplication this repo keeps
* finding. `downloadZip` stays as the zip-shaped call its callers already
* make.
*
* @returns `true` when a download or save was started; `false` when the
*   user dismissed the save picker, in which case nothing was written.
*/
async function downloadBlob(blob, filename, fileType) {
	if ("showSaveFilePicker" in window && window.showSaveFilePicker) {
		let handle = null;
		try {
			handle = await window.showSaveFilePicker({
				suggestedName: filename,
				types: [{
					description: fileType.description,
					accept: { [fileType.mimeType]: [fileType.extension] }
				}]
			});
		} catch (err) {
			if (classifyPickerRejection(err) === "cancelled") {
				log.info("User cancelled save dialog");
				return false;
			}
			log.warn("showSaveFilePicker failed, using fallback:", messageOf(err));
		}
		if (handle !== null) try {
			await writeFileOrAbort(handle, blob);
			log.info(`Saved via File System Access API: ${filename}`);
			return true;
		} catch (err) {
			if (err?.name === "AbortError") {
				log.info("User cancelled save dialog");
				return false;
			}
			log.warn("Writing the picked file failed, using fallback:", messageOf(err));
		}
	}
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.style.display = "none";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	setTimeout(() => URL.revokeObjectURL(url), 1e4);
	log.info(`Download triggered via <a download>: ${filename}`);
	return true;
}
//#endregion
export { PDF_FILE_TYPE, ZIP_FILE_TYPE, downloadBlob, downloadZip, exportSessionAsZip, exportSessionHandleAsZip, syncToExternalZip };
